/**
 * Holiday utility for calendar and gantt chart views
 * Supports multiple countries with automatic locale detection
 */

export type CountryCode = 'JP' | 'US' | 'GB' | 'DE' | 'FR' | 'CN' | 'KR'

export interface Holiday {
  date: string // YYYY-MM-DD format
  name: string
  nameEn: string
}

// Japanese holidays 2024-2026
const japaneseHolidays: Record<string, { name: string; nameEn: string }> = {
  // 2024
  '2024-01-01': { name: '元日', nameEn: "New Year's Day" },
  '2024-01-08': { name: '成人の日', nameEn: 'Coming of Age Day' },
  '2024-02-11': { name: '建国記念の日', nameEn: 'National Foundation Day' },
  '2024-02-12': { name: '振替休日', nameEn: 'Substitute Holiday' },
  '2024-02-23': { name: '天皇誕生日', nameEn: "Emperor's Birthday" },
  '2024-03-20': { name: '春分の日', nameEn: 'Vernal Equinox Day' },
  '2024-04-29': { name: '昭和の日', nameEn: 'Showa Day' },
  '2024-05-03': { name: '憲法記念日', nameEn: 'Constitution Memorial Day' },
  '2024-05-04': { name: 'みどりの日', nameEn: 'Greenery Day' },
  '2024-05-05': { name: 'こどもの日', nameEn: "Children's Day" },
  '2024-05-06': { name: '振替休日', nameEn: 'Substitute Holiday' },
  '2024-07-15': { name: '海の日', nameEn: 'Marine Day' },
  '2024-08-11': { name: '山の日', nameEn: 'Mountain Day' },
  '2024-08-12': { name: '振替休日', nameEn: 'Substitute Holiday' },
  '2024-09-16': { name: '敬老の日', nameEn: 'Respect for the Aged Day' },
  '2024-09-22': { name: '秋分の日', nameEn: 'Autumnal Equinox Day' },
  '2024-09-23': { name: '振替休日', nameEn: 'Substitute Holiday' },
  '2024-10-14': { name: 'スポーツの日', nameEn: 'Sports Day' },
  '2024-11-03': { name: '文化の日', nameEn: 'Culture Day' },
  '2024-11-04': { name: '振替休日', nameEn: 'Substitute Holiday' },
  '2024-11-23': { name: '勤労感謝の日', nameEn: 'Labor Thanksgiving Day' },
  // 2025
  '2025-01-01': { name: '元日', nameEn: "New Year's Day" },
  '2025-01-13': { name: '成人の日', nameEn: 'Coming of Age Day' },
  '2025-02-11': { name: '建国記念の日', nameEn: 'National Foundation Day' },
  '2025-02-23': { name: '天皇誕生日', nameEn: "Emperor's Birthday" },
  '2025-02-24': { name: '振替休日', nameEn: 'Substitute Holiday' },
  '2025-03-20': { name: '春分の日', nameEn: 'Vernal Equinox Day' },
  '2025-04-29': { name: '昭和の日', nameEn: 'Showa Day' },
  '2025-05-03': { name: '憲法記念日', nameEn: 'Constitution Memorial Day' },
  '2025-05-04': { name: 'みどりの日', nameEn: 'Greenery Day' },
  '2025-05-05': { name: 'こどもの日', nameEn: "Children's Day" },
  '2025-05-06': { name: '振替休日', nameEn: 'Substitute Holiday' },
  '2025-07-21': { name: '海の日', nameEn: 'Marine Day' },
  '2025-08-11': { name: '山の日', nameEn: 'Mountain Day' },
  '2025-09-15': { name: '敬老の日', nameEn: 'Respect for the Aged Day' },
  '2025-09-23': { name: '秋分の日', nameEn: 'Autumnal Equinox Day' },
  '2025-10-13': { name: 'スポーツの日', nameEn: 'Sports Day' },
  '2025-11-03': { name: '文化の日', nameEn: 'Culture Day' },
  '2025-11-23': { name: '勤労感謝の日', nameEn: 'Labor Thanksgiving Day' },
  '2025-11-24': { name: '振替休日', nameEn: 'Substitute Holiday' },
  // 2026
  '2026-01-01': { name: '元日', nameEn: "New Year's Day" },
  '2026-01-12': { name: '成人の日', nameEn: 'Coming of Age Day' },
  '2026-02-11': { name: '建国記念の日', nameEn: 'National Foundation Day' },
  '2026-02-23': { name: '天皇誕生日', nameEn: "Emperor's Birthday" },
  '2026-03-20': { name: '春分の日', nameEn: 'Vernal Equinox Day' },
  '2026-04-29': { name: '昭和の日', nameEn: 'Showa Day' },
  '2026-05-03': { name: '憲法記念日', nameEn: 'Constitution Memorial Day' },
  '2026-05-04': { name: 'みどりの日', nameEn: 'Greenery Day' },
  '2026-05-05': { name: 'こどもの日', nameEn: "Children's Day" },
  '2026-05-06': { name: '振替休日', nameEn: 'Substitute Holiday' },
  '2026-07-20': { name: '海の日', nameEn: 'Marine Day' },
  '2026-08-11': { name: '山の日', nameEn: 'Mountain Day' },
  '2026-09-21': { name: '敬老の日', nameEn: 'Respect for the Aged Day' },
  '2026-09-22': { name: '国民の休日', nameEn: "Citizen's Holiday" },
  '2026-09-23': { name: '秋分の日', nameEn: 'Autumnal Equinox Day' },
  '2026-10-12': { name: 'スポーツの日', nameEn: 'Sports Day' },
  '2026-11-03': { name: '文化の日', nameEn: 'Culture Day' },
  '2026-11-23': { name: '勤労感謝の日', nameEn: 'Labor Thanksgiving Day' },
}

// US Federal Holidays 2024-2026
const usHolidays: Record<string, { name: string; nameEn: string }> = {
  // 2024
  '2024-01-01': { name: "New Year's Day", nameEn: "New Year's Day" },
  '2024-01-15': { name: 'Martin Luther King Jr. Day', nameEn: 'Martin Luther King Jr. Day' },
  '2024-02-19': { name: "Presidents' Day", nameEn: "Presidents' Day" },
  '2024-05-27': { name: 'Memorial Day', nameEn: 'Memorial Day' },
  '2024-06-19': { name: 'Juneteenth', nameEn: 'Juneteenth' },
  '2024-07-04': { name: 'Independence Day', nameEn: 'Independence Day' },
  '2024-09-02': { name: 'Labor Day', nameEn: 'Labor Day' },
  '2024-10-14': { name: 'Columbus Day', nameEn: 'Columbus Day' },
  '2024-11-11': { name: 'Veterans Day', nameEn: 'Veterans Day' },
  '2024-11-28': { name: 'Thanksgiving Day', nameEn: 'Thanksgiving Day' },
  '2024-12-25': { name: 'Christmas Day', nameEn: 'Christmas Day' },
  // 2025
  '2025-01-01': { name: "New Year's Day", nameEn: "New Year's Day" },
  '2025-01-20': { name: 'Martin Luther King Jr. Day', nameEn: 'Martin Luther King Jr. Day' },
  '2025-02-17': { name: "Presidents' Day", nameEn: "Presidents' Day" },
  '2025-05-26': { name: 'Memorial Day', nameEn: 'Memorial Day' },
  '2025-06-19': { name: 'Juneteenth', nameEn: 'Juneteenth' },
  '2025-07-04': { name: 'Independence Day', nameEn: 'Independence Day' },
  '2025-09-01': { name: 'Labor Day', nameEn: 'Labor Day' },
  '2025-10-13': { name: 'Columbus Day', nameEn: 'Columbus Day' },
  '2025-11-11': { name: 'Veterans Day', nameEn: 'Veterans Day' },
  '2025-11-27': { name: 'Thanksgiving Day', nameEn: 'Thanksgiving Day' },
  '2025-12-25': { name: 'Christmas Day', nameEn: 'Christmas Day' },
  // 2026
  '2026-01-01': { name: "New Year's Day", nameEn: "New Year's Day" },
  '2026-01-19': { name: 'Martin Luther King Jr. Day', nameEn: 'Martin Luther King Jr. Day' },
  '2026-02-16': { name: "Presidents' Day", nameEn: "Presidents' Day" },
  '2026-05-25': { name: 'Memorial Day', nameEn: 'Memorial Day' },
  '2026-06-19': { name: 'Juneteenth', nameEn: 'Juneteenth' },
  '2026-07-03': { name: 'Independence Day (observed)', nameEn: 'Independence Day (observed)' },
  '2026-09-07': { name: 'Labor Day', nameEn: 'Labor Day' },
  '2026-10-12': { name: 'Columbus Day', nameEn: 'Columbus Day' },
  '2026-11-11': { name: 'Veterans Day', nameEn: 'Veterans Day' },
  '2026-11-26': { name: 'Thanksgiving Day', nameEn: 'Thanksgiving Day' },
  '2026-12-25': { name: 'Christmas Day', nameEn: 'Christmas Day' },
}

// Country holiday maps
const holidaysByCountry: Record<CountryCode, Record<string, { name: string; nameEn: string }>> = {
  JP: japaneseHolidays,
  US: usHolidays,
  GB: usHolidays, // Simplified: use US holidays as base for UK
  DE: usHolidays, // Simplified: use US holidays as base for Germany
  FR: usHolidays, // Simplified: use US holidays as base for France
  CN: japaneseHolidays, // Simplified: use Japanese holidays as base for China
  KR: japaneseHolidays, // Simplified: use Japanese holidays as base for Korea
}

// Country display names
export const countryNames: Record<CountryCode, { ja: string; en: string }> = {
  JP: { ja: '日本', en: 'Japan' },
  US: { ja: 'アメリカ', en: 'United States' },
  GB: { ja: 'イギリス', en: 'United Kingdom' },
  DE: { ja: 'ドイツ', en: 'Germany' },
  FR: { ja: 'フランス', en: 'France' },
  CN: { ja: '中国', en: 'China' },
  KR: { ja: '韓国', en: 'South Korea' },
}

/**
 * Detect user's country from browser locale
 */
export function detectUserCountry(): CountryCode {
  const locale = navigator.language || 'en-US'
  const countryFromLocale = locale.split('-')[1]?.toUpperCase()

  if (countryFromLocale && countryFromLocale in holidaysByCountry) {
    return countryFromLocale as CountryCode
  }

  // Default based on language
  const language = locale.split('-')[0].toLowerCase()
  switch (language) {
    case 'ja':
      return 'JP'
    case 'ko':
      return 'KR'
    case 'zh':
      return 'CN'
    case 'de':
      return 'DE'
    case 'fr':
      return 'FR'
    default:
      return 'JP' // Default to Japan for this app
  }
}

/**
 * Check if a date is a holiday
 */
export function isHoliday(date: Date, country: CountryCode = 'JP'): boolean {
  const dateKey = formatDateKey(date)
  return dateKey in holidaysByCountry[country]
}

/**
 * Get holiday info for a date
 */
export function getHoliday(date: Date, country: CountryCode = 'JP'): Holiday | null {
  const dateKey = formatDateKey(date)
  const holiday = holidaysByCountry[country][dateKey]

  if (holiday) {
    return {
      date: dateKey,
      name: holiday.name,
      nameEn: holiday.nameEn,
    }
  }

  return null
}

/**
 * Get all holidays for a date range
 */
export function getHolidaysInRange(
  startDate: Date,
  endDate: Date,
  country: CountryCode = 'JP'
): Holiday[] {
  const holidays: Holiday[] = []
  const countryHolidays = holidaysByCountry[country]

  for (const dateKey in countryHolidays) {
    const holidayDate = new Date(dateKey)
    if (holidayDate >= startDate && holidayDate <= endDate) {
      holidays.push({
        date: dateKey,
        name: countryHolidays[dateKey].name,
        nameEn: countryHolidays[dateKey].nameEn,
      })
    }
  }

  return holidays.sort((a, b) => a.date.localeCompare(b.date))
}

/**
 * Format date to YYYY-MM-DD key
 */
function formatDateKey(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Get available countries
 */
export function getAvailableCountries(): CountryCode[] {
  return Object.keys(holidaysByCountry) as CountryCode[]
}
